/**
 * Cache Storage API — serviço de pré-cache de mídias para o Player.
 *
 * INTEGRAÇÃO NO PLAYER:
 *  1. Ao carregar a playlist: mediaCache.preCache(urls)  → armazena no cache
 *  2. Ao renderizar cada slide: mediaCache.toBlobUrl(url) → src local (offline-safe)
 *  3. Ao desmontar: revogar blob URLs com URL.revokeObjectURL()
 *
 * O SW usa o mesmo MEDIA_CACHE_NAME, então as gravações feitas aqui
 * também são servidas pelo Service Worker (e vice-versa).
 *
 * COMPATIBILIDADE DE PLATAFORMA:
 *  - Cache Storage API indisponível (WebOS antigo) → todas as operações
 *    são no-op silenciosos e o Player usa a URL remota diretamente.
 *  - URL.createObjectURL indisponível ou com erro (Tizen, TV Boxes com
 *    sandbox restrita) → toBlobUrl retorna a URL original (fallback de rede).
 *  - QuotaExceededError → preCache ignora o item silenciosamente; o Player
 *    faz fallback para a URL de rede naquele ciclo.
 */

export const MEDIA_CACHE_NAME = 'player-media-cache';

/**
 * Downloads em andamento por URL. Sem isto, um `evict` durante o download apagava um cache ainda vazio
 * e o `put` do download antigo recolocava o arquivo (órfão: nenhum blob/URL ativo para um próximo
 * `evict` limpar). `evict` aborta o fetch e espera o download assentar antes de apagar.
 */
const emVoo = new Map<string, { controller: AbortController; concluido: Promise<void> }>();

export const mediaCache = {
  /**
   * Pré-cacheia uma lista de URLs em segundo plano.
   * - Idempotente: pula URLs já presentes no cache
   * - Silencioso: nunca lança exceção (não trava o player)
   */
  async preCache(urls: string[]): Promise<void> {
    if (!('caches' in window)) return;

    let cache: Cache;
    try {
      cache = await caches.open(MEDIA_CACHE_NAME);
    } catch {
      return;
    }

    await Promise.allSettled(
      urls.map(async (url) => {
        if (await cache.match(url)) return; // já está em cache, pula
        // Outra chamada já está baixando esta URL: espera ela assentar em vez de voltar antes — quem
        // chama toBlobUrl em seguida precisa encontrar o arquivo no cache.
        const emAndamento = emVoo.get(url);
        if (emAndamento) { await emAndamento.concluido; return; }

        const controller = new AbortController();
        const concluido = (async () => {
          // Usa cache:'reload' para garantir que busca da rede na 1ª vez,
          // independentemente do cache HTTP do browser
          const response = await fetch(url, { cache: 'reload', signal: controller.signal });
          if (!response.ok || controller.signal.aborted) return;

          try {
            await cache.put(url, response);
          } catch {
            // QuotaExceededError ou restrição de sandbox (Smart TV) —
            // ignora silenciosamente; o Player usará a URL remota neste ciclo.
          }
        })()
          .catch(() => { /* rede/abort: o Player usa a URL remota neste ciclo */ })
          .finally(() => {
            if (emVoo.get(url)?.controller === controller) emVoo.delete(url);
          });

        emVoo.set(url, { controller, concluido });
        await concluido;
      })
    );
  },

  /**
   * Converte uma URL de mídia em um Blob URL local.
   *
   * Fluxo:
   *  1. Tenta abrir o cache e buscar a URL
   *  2. Se encontrada e Blob URLs suportadas → cria e retorna blob://...
   *  3. Qualquer falha → retorna a URL original (fallback para rede/SW)
   *
   * Os blob URLs criados aqui NÃO são gerenciados internamente.
   * O chamador é responsável por chamar URL.revokeObjectURL() ao descartar.
   */
  async toBlobUrl(url: string): Promise<string> {
    if (!('caches' in window)) return url;

    try {
      const cache = await caches.open(MEDIA_CACHE_NAME);
      const response = await cache.match(url);
      if (!response) return url; // não cacheado ainda → usa rede

      const blob = await response.blob();

      // Guard: TV Boxes com sandbox restrita podem não ter URL.createObjectURL.
      // Verificação explícita em vez de feature-detect pós-execução.
      if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
        return url; // bypass direto para a URL remota
      }

      try {
        return URL.createObjectURL(blob);
      } catch {
        // QuotaExceededError ou SecurityError (sandbox Tizen/WebOS) →
        // fallback transparente para a URL original de streaming.
        return url;
      }
    } catch {
      return url; // qualquer erro → fallback para URL original
    }
  },

  /**
   * Remove URLs do cache que não fazem mais parte da playlist.
   * Chame quando mídias forem deletadas via Realtime. Aborta o download em andamento da URL, se houver.
   */
  async evict(urls: string[]): Promise<void> {
    if (!('caches' in window) || urls.length === 0) return;
    try {
      const cache = await caches.open(MEDIA_CACHE_NAME);
      await Promise.allSettled(urls.map(async (url) => {
        const voo = emVoo.get(url);
        if (voo) {
          voo.controller.abort();
          await voo.concluido; // o download antigo precisa assentar antes do delete
        }
        await cache.delete(url);
      }));
    } catch {
      // ignore
    }
  },
};
